import axios from 'axios'
import { getApiBaseUrlWithoutV1 } from '../../../setup/SetupAxios'
import type { BidDriverInput } from '@numeo/types'
import type { ServerEmailAnalyticsPayload } from '../utils/loadAttributionAnalytics'

interface SendPrivateLoadEmailResponse {
    success: boolean
    result?: {
        emailThreadId?: string
        load?: { _id?: unknown }
    }
    /** True when the backend returned an existing send instead of contacting the provider again. */
    alreadySent?: boolean
    message?: string
}

interface SendOfferBookEmailResponse {
    success: boolean
    result?: {
        emailThreadId?: string
        load?: { _id?: unknown }
    }
    message?: string
    /** True when the backend deduplicated an idempotent retry. */
    alreadySent?: boolean
}

/** Fetch emails for a specific load */
export const fetchLoadEmails = async (emailThreadId: string, loadId: string) => {
    const response = await axios.get('/trucks/loads/emails', {
        params: { emailThreadId, loadId },
    })
    return response.data
}

/** Send an email reply to a thread */
export const sendEmailReply = async (
    threadId: string,
    data: { to: { email: string }; subject: string; htmlBody: string; messageId?: string; emailReplyPreference?: 'MANUAL' | 'AUTO' }
) => {
    const formData = new FormData()
    formData.append('to', JSON.stringify(data.to))
    formData.append('subject', data.subject)
    formData.append('htmlBody', data.htmlBody)
    formData.append('emailReplyPreference', data.emailReplyPreference || 'MANUAL')
    if (data.messageId) formData.append('messageId', data.messageId)

    const response = await axios.post(`/trucks/send-email-with-attachments/${threadId}`, formData)
    return response.data
}

/** Mark a load's emails as read */
export const markLoadAsRead = async (loadId: string) => {
    const response = await axios.patch(`/ext/loads/${loadId}/read`)
    return response.data
}

/** Report an email problem */
export const reportEmailProblem = async (data: { threadId?: string; loadId?: string; tabId?: string; message?: string }) => {
    const response = await axios.post('/email/report-problem', data)
    return response.data
}

/** Send the first email about a load (ADR-037.1); the server names the listing it is about from the load. */
export const sendPrivateLoadEmailV4 = async (payload: {
    load: unknown
    action: { type: string; callType: string }
    email: {
        to: { email: string }
        subject: string
        htmlBody: string
        emailType: string
        emailReplyPreference: 'AUTO' | 'MANUAL'
        cc: string[]
        senderEmail?: string
    }
    datSearchId: string
    userId: string
    loadHubAnalytics?: ServerEmailAnalyticsPayload
}) => {
    const response = await axios.post<SendPrivateLoadEmailResponse>(`${getApiBaseUrlWithoutV1()}/email/loads/messages/v1`, { ...payload, kind: 'INQUIRY' })
    return response.data
}

/** Send a canonical offer/book/info email from the shared compose UI. */
export const sendOfferBookEmailCanonical = async (payload: {
    load: unknown
    action: { type: string; callType: string }
    email: {
        to: { email: string }
        toRecipients: string[]
        subject: string
        htmlBody: string
        emailType: string
        emailReplyPreference: 'MANUAL'
        cc: string[]
        senderEmail: string
    }
    datSearchId: string
    userId: string
    loadHubAnalytics?: ServerEmailAnalyticsPayload
    /**
     * The rate being asked for, so the server can record the bid.
     *
     * Sent with the email rather than posted after it: the figure lives in the body, which the server
     * hands to email-service unparsed, so this is the only moment it can be captured — and a record
     * written inside the send cannot be lost to a closed tab the way a follow-up request can. Omitted
     * on an info enquiry, which asks for nothing.
     */
    bidRate?: number
    /** The body was hand-edited, so `bidRate` is the calculator's figure, not provably the sent one. */
    bodyEdited?: boolean
    /**
     * The driver this bid is for — the unit whose details the compose put in the body.
     *
     * Sent only when the operator actually PICKED one. The server re-checks the id's shape and drops
     * the pair if it fails, so a name here is never trusted on its own.
     */
    bidDriver?: BidDriverInput
}): Promise<SendOfferBookEmailResponse> => {
    const response = await axios.post<SendOfferBookEmailResponse>('/ext/offer-book-email-canonical', payload)
    return response.data
}
